import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styles from 'components/quests/knov-agent-button.module.scss'
import cn from 'classnames'
import WithTooltip from 'components/shared/WithTooltip'
import { isMobile } from 'lib/device'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useStore from 'state/knovStore'
import {
    getAgentModelConfig,
    getAgentModelDisplayLabel,
    getConfiguredAgentModels,
    getDefaultAgentModel,
    getSupportedLlmModels,
    resolveCanonicalAgentModelId,
} from 'lib/agentModels'
import { isPanelCenter } from 'state/imperativeApis/swiperApi'
import ReactQuill, { Quill } from 'react-quill-new'
import { Bot, ChevronLeft } from 'lucide-react'

// Access Delta through Quill.import
const Delta = Quill.import('delta')

export default function KnovAgentButtonUIMain({
    models = null,
    contextStyles,
    active,
    imageStyles,
    setAgentModel,
    editorRef = null,
    onMenuStateChange = null,
}) {
    const { panelId, hide } = usePanelContext()
    const agentModelConfig = useStore(state => state.agentModelConfig)
    const resolvedAgentModelConfig = getAgentModelConfig(agentModelConfig)
    const supportedLlmModels = getSupportedLlmModels(resolvedAgentModelConfig)
    const defaultModel = getDefaultAgentModel(resolvedAgentModelConfig)
    const configuredModels = getConfiguredAgentModels(resolvedAgentModelConfig)
    const mainModelIds = configuredModels
        .filter(model => model.group === 'flagship')
        .map(model => model.id)
        .filter((modelName, index, modelsList) => {
            return (
                Boolean(modelName) &&
                supportedLlmModels.includes(modelName) &&
                modelsList.indexOf(modelName) === index
            )
        })
    const panel = useStore.getState().panels.getPanel(panelId)
    const [showMenu, setShowMenu] = useState(false)
    const [agentHover, setAgentHover] = useState(false)
    const [menuDirection, setMenuDirection] = useState('up')
    const [menuMaxHeight, setMenuMaxHeight] = useState(0)
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({
        opacity: 0,
        position: 'fixed',
        top: '-9999px',
    })
    const [currentLevel, setCurrentLevel] = useState<1 | 2>(1)
    const [showOtherModels, setShowOtherModels] = useState(false)
    const buttonRef = useRef(null)
    const menuRef = useRef(null)
    // 85 is the height of the control panel in the center panel, other panels have no control panel.
    const CONTROL_TOP = isPanelCenter(panelId) ? 85 : 0
    // Safety padding to avoid menu being flush with screen edges
    const SAFETY_PADDING = 60

    const isMobile = window.innerWidth <= 500

    // Define which models show in the main menu
    // Filter models into main and other categories
    const modelsList = Array.isArray(models) ? models : supportedLlmModels
    const normalizedModels = modelsList
        .map(model => resolveCanonicalAgentModelId(model, resolvedAgentModelConfig))
        .filter((model): model is string => Boolean(model))
        .filter((model, index, modelsList) => modelsList.indexOf(model) === index)
    const normalizedActiveModel = resolveCanonicalAgentModelId(active, resolvedAgentModelConfig)
    const mainMenuModels = normalizedModels.filter(model => mainModelIds.includes(model))
    const otherMenuModels = normalizedModels.filter(model => !mainModelIds.includes(model))

    // Notify parent component when menu state changes
    useEffect(() => {
        // Only notify after a short delay - enough time to let the menu render and become scrollable
        if (onMenuStateChange) {
            if (showMenu) {
                // For opening the menu, wait until it's fully initialized before notifying parent
                const timer = setTimeout(() => {
                    onMenuStateChange(true)
                }, 50)
                return () => clearTimeout(timer)
            } else {
                // For closing, notify immediately
                onMenuStateChange(false)
            }
        }
    }, [showMenu, onMenuStateChange])

    // The menu takes focus while it is open (iOS scrolls it only then), so the
    // keyboard goes. A choice is part of writing: on a phone, give the editor its
    // focus back inside the same tap, the only moment iOS lets the keyboard return,
    // instead of leaving the pill to fold under a dropped keyboard.
    const chooseAgent = model => {
        setAgentModel(model)
        setShowMenu(false)
        setCurrentLevel(1)
        if (isMobile) editorRef?.current?.focus?.()
    }

    // Add click outside handler to close the menu
    useEffect(() => {
        if (showMenu) {
            const handleClickOutside = event => {
                if (
                    menuRef.current &&
                    !menuRef.current.contains(event.target) &&
                    buttonRef.current &&
                    !buttonRef.current.contains(event.target)
                ) {
                    setShowMenu(false)
                    setCurrentLevel(1)
                    setShowOtherModels(false)
                }
            }

            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('touchstart', handleClickOutside)

            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
                document.removeEventListener('touchstart', handleClickOutside)
            }
        }
    }, [showMenu])

    useEffect(() => {
        if (showMenu && buttonRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect()
            const spaceAbove = buttonRect.top - SAFETY_PADDING
            const spaceBelow = window.innerHeight - buttonRect.bottom - CONTROL_TOP - SAFETY_PADDING

            // Calculate max heights for both directions
            const maxHeightAbove = spaceAbove
            const maxHeightBelow = spaceBelow

            // Choose direction with more space
            if (maxHeightBelow >= maxHeightAbove) {
                setMenuDirection('down')
                setMenuMaxHeight(maxHeightBelow)
            } else {
                setMenuDirection('up')
                setMenuMaxHeight(maxHeightAbove)
            }

            // Auto-focus the menu after it renders and simulate a touch for iOS
            setTimeout(() => {
                if (menuRef.current) {
                    // Focus the menu
                    menuRef.current.focus()

                    if (isMobile) {
                        // For iOS, force the scroll container to be initialized
                        // by programmatically scrolling it a tiny amount and back
                        const menu = menuRef.current
                        const originalScroll = menu.scrollTop
                        menu.scrollTop = 1
                        setTimeout(() => {
                            menu.scrollTop = originalScroll
                        }, 5)

                        // Force iOS to recognize this as a scrollable area
                        try {
                            // Try to scroll to activate iOS momentum scrolling
                            menu.style.webkitOverflowScrolling = 'auto'
                            setTimeout(() => {
                                menu.style.webkitOverflowScrolling = 'touch'
                            }, 10)
                        } catch (e) {
                            // Ignore any errors in case of older browsers
                        }
                    }
                }
            }, 10)
        }
    }, [showMenu, CONTROL_TOP])

    useEffect(() => {
        if (!showMenu || !buttonRef.current) {
            setMenuStyle(prev => ({ ...prev, opacity: 0, top: '-9999px' }))
            return
        }

        const buttonRect = buttonRef.current.getBoundingClientRect()
        const estimatedMenuWidth = Math.max(menuRef.current?.offsetWidth || 0, 220)
        const gap = 8
        let left = buttonRect.right - estimatedMenuWidth
        if (left < SAFETY_PADDING) left = SAFETY_PADDING
        if (left + estimatedMenuWidth > window.innerWidth - SAFETY_PADDING) {
            left = window.innerWidth - estimatedMenuWidth - SAFETY_PADDING
        }

        const nextStyle: React.CSSProperties = {
            position: 'fixed',
            left: `${left}px`,
            width: `${estimatedMenuWidth}px`,
            maxHeight: `${Math.max(140, menuMaxHeight)}px`,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            zIndex: 10000,
            opacity: 1,
        }

        if (menuDirection === 'down') {
            nextStyle.top = `${buttonRect.bottom + gap}px`
            nextStyle.bottom = 'auto'
        } else {
            nextStyle.bottom = `${window.innerHeight - buttonRect.top + gap}px`
            nextStyle.top = 'auto'
        }

        setMenuStyle(nextStyle)
    }, [showMenu, menuDirection, menuMaxHeight, currentLevel, SAFETY_PADDING])

    // Add enhanced touch handling for the menu
    useEffect(() => {
        if (showMenu && menuRef.current) {
            // Setup enhanced touch handlers
            const menu = menuRef.current
            let isScrolling = false
            let touchInProgress = false

            // Manual scroll handler function
            const handleTouchStart = e => {
                // Don't stop propagation for clicks on menu items
                if (e.target.closest(`.${styles.knovAgentMenuItem}`)) {
                    return
                }

                e.stopPropagation()
                touchInProgress = true
                // Focus menu on touch to ensure scroll works
                menu.focus()
            }

            const handleTouchMove = e => {
                e.stopPropagation()
                isScrolling = true
            }

            const handleTouchEnd = e => {
                // Don't interfere with clicks on menu items
                if (e.target.closest(`.${styles.knovAgentMenuItem}`)) {
                    return
                }

                touchInProgress = false
                // Reset scrolling flag after a delay to allow blur handling to check it
                setTimeout(() => {
                    isScrolling = false
                }, 100)
            }

            // Track scrolling state
            const handleScroll = () => {
                isScrolling = true
                // Reset scrolling flag after scroll stops
                clearTimeout(menu.scrollTimeout)
                menu.scrollTimeout = setTimeout(() => {
                    isScrolling = false
                }, 150)
            }

            // Custom blur handler
            const handleBlur = e => {
                // Don't handle blur if the related target is a menu item
                if (e.relatedTarget && e.relatedTarget.closest(`.${styles.knovAgentMenuItem}`)) {
                    return
                }

                // If we're scrolling or touch is in progress, don't close the menu
                if (isScrolling || touchInProgress) {
                    // Prevent blur by re-focusing
                    setTimeout(() => menu.focus(), 0)
                    return
                }

                // Otherwise, close the menu
                setShowMenu(false)
            }

            // Use DOM event listeners for better control
            menu.addEventListener('touchstart', handleTouchStart, { passive: false })
            menu.addEventListener('touchmove', handleTouchMove, { passive: true })
            menu.addEventListener('touchend', handleTouchEnd, { passive: true })
            menu.addEventListener('scroll', handleScroll, { passive: true })
            menu.addEventListener('blur', handleBlur)

            // Auto-focus the menu immediately
            menu.focus()

            return () => {
                // Clean up
                menu.removeEventListener('touchstart', handleTouchStart)
                menu.removeEventListener('touchmove', handleTouchMove)
                menu.removeEventListener('touchend', handleTouchEnd)
                menu.removeEventListener('scroll', handleScroll)
                menu.removeEventListener('blur', handleBlur)
                clearTimeout(menu.scrollTimeout)
            }
        }
    }, [showMenu])

    // Prevent native browser pull-to-refresh when menu is open
    useEffect(() => {
        if (!showMenu) return

        // Use WeakMap to store touch positions without modifying the DOM element
        const touchStartMap = new WeakMap<HTMLElement, number>()

        // Store initial touch position
        const handleTouchStart = (e: TouchEvent) => {
            const touch = e.touches[0]
            if (touch) {
                touchStartMap.set(document.body, touch.clientY)
            }
        }

        // Prevent browser default for pull-down gestures
        const preventDefaultPullToRefresh = (e: TouchEvent) => {
            // Get touch position
            const touch = e.touches[0]
            if (!touch) return

            // Get initial touch position from WeakMap
            const startY = touchStartMap.get(document.body)
            if (startY === undefined) return

            // Calculate direction (positive = pulling down)
            const deltaY = touch.clientY - startY
            const isPullingDown = deltaY > 0

            // When menu is open, always prevent pull-to-refresh
            if (isPullingDown && menuRef.current) {
                e.preventDefault()
            }
        }

        // Reset touch position tracking
        const resetTouchStart = () => {
            touchStartMap.delete(document.body)
        }

        // Add touch event listeners to document body to catch all touches
        document.body.addEventListener('touchstart', handleTouchStart, { passive: true })
        document.body.addEventListener('touchmove', preventDefaultPullToRefresh, { passive: false })
        document.body.addEventListener('touchend', resetTouchStart, { passive: true })
        document.body.addEventListener('touchcancel', resetTouchStart, { passive: true })

        return () => {
            // Clean up event listeners
            document.body.removeEventListener('touchstart', handleTouchStart)
            document.body.removeEventListener('touchmove', preventDefaultPullToRefresh)
            document.body.removeEventListener('touchend', resetTouchStart)
            document.body.removeEventListener('touchcancel', resetTouchStart)
            touchStartMap.delete(document.body)
        }
    }, [showMenu])

    const activeStyles = active || (!isMobile && agentHover) ? styles.active : null

    return (
        <div className={styles.knovAgentButtonContainer}>
            <div
                className={cn(styles.knovAgentButtonUIMainComp, contextStyles, activeStyles)}
                ref={buttonRef}
                onMouseEnter={() => !isMobile && setAgentHover(true)}
                onMouseLeave={() => !isMobile && setAgentHover(false)}
            >
                <WithTooltip tip="Choose agent model.">
                    <div
                        className={cn(styles.toggleAgent)}
                        onClick={() => setShowMenu(!showMenu)}
                        tabIndex={-1}
                        onMouseDown={e => e.preventDefault()}
                    >
                        <Bot aria-hidden="true" />
                    </div>
                </WithTooltip>
            </div>

            {showMenu &&
                createPortal(
                    <div
                        className={cn(
                            styles.knovAgentMenu,
                            styles.knovAgentMenuPortal,
                            menuDirection === 'down' && styles.knovAgentMenuDown,
                            currentLevel === 1 && styles.knovAgentMenuPadded,
                        )}
                        style={menuStyle}
                        ref={node => {
                            if (node && !menuRef.current && isMobile) {
                                menuRef.current = node
                                requestAnimationFrame(() => {
                                    // @ts-expect-error vendor prefix not in CSSStyleDeclaration
                                    node.style.webkitOverflowScrolling = 'touch'
                                    node.scrollTop = 1
                                    requestAnimationFrame(() => {
                                        node.focus()
                                        node.scrollTop = 0
                                    })
                                })
                            } else {
                                menuRef.current = node
                            }
                        }}
                        onScroll={e => e.stopPropagation()}
                        tabIndex={0}
                    >
                        {currentLevel === 1 ? (
                            <>
                                {/* The bot button only opens this menu, so leaving
                                    the agent lives here rather than on a toggle. */}
                                {normalizedActiveModel && (
                                    <div
                                        className={cn(
                                            styles.knovAgentMenuItem,
                                            styles.removeAgentItem,
                                        )}
                                        onClick={e => {
                                            e.stopPropagation()
                                            chooseAgent(null)
                                        }}
                                        tabIndex={-1}
                                        onMouseDown={e => e.preventDefault()}
                                    >
                                        Remove agent
                                    </div>
                                )}
                                {mainMenuModels.map((model, index) => {
                                    const activeModelStyles =
                                        normalizedActiveModel === model ||
                                        (!normalizedActiveModel && defaultModel === model)
                                            ? styles.activeModel
                                            : null
                                    return (
                                        <div
                                            key={index}
                                            className={cn(
                                                styles.knovAgentMenuItem,
                                                activeModelStyles,
                                            )}
                                            onClick={e => {
                                                e.stopPropagation()
                                                chooseAgent(model)
                                            }}
                                            tabIndex={-1}
                                            onMouseDown={e => e.preventDefault()}
                                        >
                                            {getAgentModelDisplayLabel(
                                                model,
                                                resolvedAgentModelConfig,
                                            )}
                                        </div>
                                    )
                                })}
                                {otherMenuModels.length > 0 && (
                                    <div
                                        className={cn(styles.knovAgentMenuItem, styles.submenuItem)}
                                        onClick={e => {
                                            e.stopPropagation()
                                            setCurrentLevel(2)
                                            setShowOtherModels(true)
                                        }}
                                        tabIndex={-1}
                                        onMouseDown={e => e.preventDefault()}
                                    >
                                        <span>Other models</span>
                                        <ChevronLeft
                                            size={16}
                                            style={{
                                                transform: 'rotate(180deg)',
                                                marginLeft: 'auto',
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={styles.submenuContainer}>
                                <div className={styles.submenuHeader}>
                                    <div
                                        className={styles.backButtonArea}
                                        onClick={e => {
                                            e.stopPropagation()
                                            setCurrentLevel(1)
                                            setShowOtherModels(false)
                                        }}
                                        tabIndex={-1}
                                        onMouseDown={e => e.preventDefault()}
                                    >
                                        <ChevronLeft size={22} className={styles.backButtonIcon} />
                                    </div>
                                    <div className={styles.submenuHeaderTitleArea}>
                                        <span className={styles.submenuHeaderText}>
                                            Other Models
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.submenuItemList}>
                                    {otherMenuModels.map((model, index) => {
                                        const activeModelStyles =
                                            normalizedActiveModel === model ||
                                            (!normalizedActiveModel && defaultModel === model)
                                                ? styles.activeModel
                                                : null
                                        return (
                                            <div
                                                key={index}
                                                className={cn(
                                                    styles.knovAgentMenuItem,
                                                    activeModelStyles,
                                                )}
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    chooseAgent(model)
                                                    setShowOtherModels(false)
                                                }}
                                                tabIndex={-1}
                                                onMouseDown={e => e.preventDefault()}
                                            >
                                                {getAgentModelDisplayLabel(
                                                    model,
                                                    resolvedAgentModelConfig,
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    )
}
